import { useState } from "react";
import { changePassword } from "./nativeProfile";
import { Spinner } from "./PageLoader";

// A "blank" banner is the placeholder asset — shown grayscale like the community
// hero so default headers read as monochrome rather than a flat colour block.
const isEmptyBanner = (url) =>
  !url || /\/(signin-empty|logo-empty)\.png(\?|$)/i.test(url);

/**
 * Change-password modal for a signed-in community member.
 * Verifies the current password server-side and updates to a new one.
 */
// Matches the server's own minimum for a new password.
const MIN_PASSWORD = 6;

// `embedded` renders the same form as a page card (Spotlight's Change Password
// page) instead of a popup: no overlay, no close button, no Cancel.
function ChangePasswordModal({ token, member, primary = "#0274F7", banner, onClose, embedded = false }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ type: "", text: "" });
  // Keyed by field, so each message renders under its own input. The banner is
  // left for server responses, which belong to no single field.
  const [errors, setErrors] = useState({});
  const clearError = (k) => setErrors((e) => (e[k] ? { ...e, [k]: "" } : e));

  const save = async (e) => {
    e.preventDefault();
    setMsg({ type: "", text: "" });

    // Collect every problem in one pass. Bailing on the first meant a blank
    // form reported "fill in all fields", then the length rule, then the match
    // rule — three submits to learn three things, none of them tied to a box.
    const next_ = {};
    if (!current) next_.current = "Current password is required.";
    if (!next) {
      next_.next = "New password is required.";
    } else if (next.length < MIN_PASSWORD) {
      next_.next = `New password must be at least ${MIN_PASSWORD} characters.`;
    } else if (next === current) {
      next_.next = "New password must be different from the current one.";
    }
    if (!confirm) {
      next_.confirm = "Please re-enter the new password.";
    } else if (next && next !== confirm) {
      next_.confirm = "Passwords do not match.";
    }

    if (Object.keys(next_).length) {
      setErrors(next_);

      return;
    }
    setErrors({});
    setSaving(true);
    try {
      const res = { data: await changePassword({
        funnelId: member?.funnel_id,
        memberId: member?.community_member_id,
        jwt: member?.member_token,
        currentPassword: current,
        newPassword: next,
      }) };
      if (res.data?.error === 0) {
        setMsg({ type: "success", text: res.data?.msg || "Password changed." });
        setCurrent("");
        setNext("");
        setConfirm("");
      } else {
        setMsg({ type: "error", text: res.data?.msg || "Could not change password." });
      }
    } catch (err) {
      setMsg({ type: "error", text: err.response?.data?.msg || "Something went wrong." });
    } finally {
      setSaving(false);
    }
  };

  // Live validation cues for the premium new-password field.
  const lenOk = next.length >= MIN_PASSWORD;
  const matchOk = confirm.length > 0 && next === confirm;

  return (
    <div
      className={embedded ? "w-full" : "fixed inset-0 z-50 flex items-stretch sm:items-center justify-center bg-ink/60 backdrop-blur-md p-0 sm:p-4"}
      /* No backdrop-click close — a stray click must not discard a
         half-entered password. Close via the X or Cancel. */
    >
      <style>{`
        @keyframes cpwPop { from { opacity: 0; transform: translateY(16px) scale(.98) } to { opacity: 1; transform: translateY(0) scale(1) } }
      `}</style>
      <form
        onSubmit={save}
        className={embedded
          ? "relative bg-surface w-full rounded-2xl border border-gray-200 shadow-sm flex flex-col overflow-hidden"
          : "relative bg-surface w-full h-full sm:h-auto sm:max-w-md rounded-none sm:rounded-3xl shadow-2xl sm:ring-1 sm:ring-black/5 max-h-none sm:max-h-[94vh] flex flex-col overflow-hidden animate-[cpwPop_.28s_cubic-bezier(.22,1,.36,1)]"}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Fixed branded header ── */}
        <div className="relative shrink-0 px-6 pt-7 pb-6 text-center overflow-hidden bg-ink">
          {banner && (
            <div
              aria-hidden="true"
              className={`absolute inset-0 z-0 bg-center bg-cover${
                isEmptyBanner(banner) ? " grayscale" : ""
              }`}
              style={{ backgroundImage: `url("${banner}")` }}
            />
          )}
          <div
            aria-hidden="true"
            className="absolute inset-0 z-0"
            style={{ background: `linear-gradient(160deg, ${primary}4D 0%, ${primary}C2 100%)` }}
          />
          {!embedded && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="absolute top-3.5 right-3.5 z-10 w-9 h-9 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/35 backdrop-blur text-white transition"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" className="w-4 h-4">
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          )}
          <div className="relative z-10 w-14 h-14 mx-auto rounded-2xl bg-surface shadow-md flex items-center justify-center" style={{ color: primary }}>
            <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h3 className="text-white text-lg font-bold mt-3 relative z-10" style={{ textShadow: "0 1px 4px rgba(0,0,0,0.4)" }}>Change password</h3>
          <p className="text-white/95 text-sm mt-0.5 relative z-10" style={{ textShadow: "0 1px 3px rgba(0,0,0,0.4)" }}>{member?.email}</p>
        </div>

        {/* ── Scrollable body ── */}
        <div className="flex-1 overflow-y-auto scrollbar-hide px-6 py-5 space-y-4 bg-gray-50/50">
          {msg.text && (
            <div
              className={`text-sm rounded-xl px-3.5 py-2.5 ${
                msg.type === "success"
                  ? "bg-green-50 dark:bg-green-500/15 text-green-700 dark:text-green-300 ring-1 ring-green-100 dark:ring-green-500/30"
                  : "bg-red-50 dark:bg-red-500/15 text-red-600 dark:text-red-300 ring-1 ring-red-100 dark:ring-red-500/30"
              }`}
            >
              {msg.text}
            </div>
          )}

          <Field
            label="Current password"
            value={current}
            onChange={(v) => { setCurrent(v); clearError("current"); }}
            disabled={saving}
            primary={primary}
            error={errors.current}
          />
          <Field label="New password" value={next} onChange={(v) => { setNext(v); clearError("next"); }} disabled={saving} primary={primary}
            error={errors.next}
            hint={next.length > 0 && (
              <span className={lenOk ? "text-green-600 dark:text-green-300" : "text-gray-400"}>
                {lenOk ? "✓ Strong enough" : `At least ${MIN_PASSWORD} characters`}
              </span>
            )}
          />
          <Field label="Confirm new password" value={confirm} onChange={(v) => { setConfirm(v); clearError("confirm"); }} disabled={saving} primary={primary}
            error={errors.confirm}
            hint={confirm.length > 0 && (
              <span className={matchOk ? "text-green-600 dark:text-green-300" : "text-red-500"}>
                {matchOk ? "✓ Passwords match" : "Passwords don't match yet"}
              </span>
            )}
          />
        </div>

        {/* ── Pinned footer ── */}
        <div className="shrink-0 border-t border-gray-100 bg-surface px-6 py-4 flex items-center justify-end gap-3">
          {!embedded && (
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="text-sm font-semibold text-gray-500 hover:text-gray-700 px-4 py-2.5 rounded-xl transition disabled:opacity-60"
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={saving}
            style={{ backgroundColor: primary, color: "var(--brand-btn-text, #fff)" }}
            className="flex items-center justify-center gap-2 text-white font-semibold px-6 py-2.5 rounded-xl hover:opacity-90 active:scale-[.98] transition disabled:opacity-60 shadow-sm"
          >
            {saving && <Spinner size={16} />}
            {saving ? "Saving…" : "Change password"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, value, onChange, disabled, primary, hint, error }) {
  const [focused, setFocused] = useState(false);
  const [show, setShow] = useState(false);
  return (
    <label className="block">
      <span className="text-[13px] font-medium text-gray-600 mb-1.5 block">{label}</span>
      <div className="relative">
        <input
          type={show ? "text" : "password"}
          value={value}
          disabled={disabled}
          autoComplete="off"
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onChange={(e) => onChange(e.target.value)}
          aria-invalid={error ? "true" : undefined}
          className="w-full rounded-xl border bg-surface px-3.5 py-2.5 pr-11 text-sm text-gray-800 outline-none"
          style={{
            borderColor: error ? "#dc2626" : focused ? primary : "rgb(var(--c-gray-200))",
            boxShadow: error
              ? "0 0 0 3px rgba(220,38,38,0.12)"
              : focused
              ? `0 0 0 3px ${primary}22`
              : "none",
          }}
        />
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          aria-label={show ? "Hide password" : "Show password"}
          className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-400 hover:text-gray-600"
        >
          {show ? (
            <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 3l18 18M10.6 10.6a2 2 0 002.8 2.8M9.9 4.2A9.6 9.6 0 0112 4c5 0 9 4 10 8a11 11 0 01-2.2 3.4M6.1 6.1A11 11 0 002 12c1 4 5 8 10 8 1.4 0 2.7-.3 3.9-.8" />
            </svg>
          ) : (
            <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
              <path strokeLinecap="round" strokeLinejoin="round" d="M2 12s4-8 10-8 10 8 10 8-4 8-10 8-10-8-10-8z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
          )}
        </button>
      </div>
      {/* An error replaces the live hint — showing "At least 6 characters" next
          to "New password is required." says the same thing twice. */}
      {error ? (
        <span className="mt-1.5 block text-xs font-medium text-red-600 dark:text-red-300">{error}</span>
      ) : hint ? (
        <span className="mt-1.5 block text-xs font-medium">{hint}</span>
      ) : null}
    </label>
  );
}

export default ChangePasswordModal;
